import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL === "/api" ? "" : (process.env.REACT_APP_BACKEND_URL || "http://localhost:49911");

const DEFAULT_CONFIG = {
    site_title: "",
    site_description: "",
    site_url: "",
    title_template: "%title% | %site%",
    meta_separator: "|",
    default_og_image: "",
    default_twitter_card: "summary_large_image",
    default_robots: "index,follow",
    focus_keyword: "",
    organization_name: "",
    organization_logo: "",
    google_search_console_verification: "",
    ga_id: "",
    pixel_id: "",
    robots_txt: "User-agent: *\nDisallow:",
};

const SeoConfigContext = createContext(DEFAULT_CONFIG);

function setMeta(name, value) {
    if (!value) return;
    let el = document.head.querySelector(`meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", value);
}

function removeMeta(name) {
    const el = document.head.querySelector(`meta[name="${name}"]`);
    if (el) el.remove();
}

function setStructuredData(id, payload) {
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    if (!payload) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.textContent = JSON.stringify(payload);
    document.head.appendChild(script);
}

function applyGlobals(config) {
    if (config.google_search_console_verification) {
        setMeta("google-site-verification", config.google_search_console_verification);
    } else {
        removeMeta("google-site-verification");
    }

    const organizationName = config.organization_name || config.site_title;
    const siteUrl = config.site_url?.trim();
    if (organizationName && siteUrl) {
        setStructuredData("global-organization-jsonld", {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: organizationName,
            url: siteUrl,
            logo: config.organization_logo || undefined,
        });
    } else {
        setStructuredData("global-organization-jsonld", null);
    }
}

export function SeoConfigProvider({ children }) {
    const [config, setConfig] = useState(DEFAULT_CONFIG);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/config/seo`);
                if (!mounted || !res.data) return;
                const merged = { ...DEFAULT_CONFIG, ...res.data };
                setConfig(merged);
                applyGlobals(merged);
            } catch {
                applyGlobals(DEFAULT_CONFIG);
            }
        };
        void load();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        applyGlobals(config);
    }, [config]);

    const value = useMemo(() => config, [config]);
    return <SeoConfigContext.Provider value={value}>{children}</SeoConfigContext.Provider>;
}

export function useSeoConfig() {
    return useContext(SeoConfigContext);
}

